/*
  ltt-quick-view — the product card's quick-view popup (Hamdi 2026-08-16).

  One <dialog> for the whole page, created on first use and reused after. Every
  card's cart button carries `data-ltt-quickview` + `data-product-url`; the
  markup comes from sections/ltt-quick-view.liquid, fetched through the Section
  Rendering API.

  Speed, in the order it matters:
    • Native <dialog> + showModal() — focus trap, Escape and the backdrop are
      the browser's job, not ours, so there is no focus-management code to run.
    • Responses are cached per URL in a Map. Reopening a card is instant.
    • Prefetch on pointerenter/touchstart, so by the time the click lands the
      HTML is usually already in the cache. Prefetches are deduped by the same
      Map holding an in-flight promise.

  The dialog closes itself on cartUpdate: Dawn's product-form.js publishes that
  event and THEN calls cart.renderContents(), which opens the cart drawer. A
  <dialog> opened with showModal() sits in the browser's top layer, so a drawer
  opened underneath it would be invisible and unclickable. Subscribers run
  synchronously inside publish(), which is why closing here beats the drawer
  opening — order, not a timeout.
*/
(function () {
  var SECTION = 'ltt-quick-view';
  var cache = new Map();
  var dialog = null;
  var body = null;
  var lastTrigger = null;

  function buildDialog() {
    if (dialog) return dialog;
    dialog = document.createElement('dialog');
    dialog.className = 'ltt-qv-dialog';
    dialog.innerHTML =
      '<div class="ltt-qv-dialog__inner">' +
      '<button type="button" class="ltt-qv-dialog__close" data-ltt-qv-close aria-label="' +
      (document.documentElement.lang === 'ar' ? 'إغلاق' : 'Close') +
      '">' +
      '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<div class="ltt-qv-dialog__body" data-ltt-qv-body>' +
      '<div class="ltt-qv-dialog__loading"><span class="ltt-qv-dialog__spinner" aria-hidden="true"></span></div>' +
      '</div>' +
      '</div>';
    document.body.appendChild(dialog);
    body = dialog.querySelector('[data-ltt-qv-body]');

    /* Backdrop click. A click on the backdrop reports the <dialog> itself as
       the target, because the backdrop is a pseudo-element of it — anything
       inside reports a descendant. */
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
      if (event.target.closest('[data-ltt-qv-close]')) close();
    });

    /* Native close (Escape) still has to restore focus and clear the body. */
    dialog.addEventListener('close', function () {
      dialog.classList.remove('is-open');
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    });

    return dialog;
  }

  function fetchMarkup(url) {
    if (cache.has(url)) return cache.get(url);
    var joiner = url.indexOf('?') === -1 ? '?' : '&';
    var promise = fetch(url + joiner + 'section_id=' + SECTION)
      .then(function (response) {
        if (!response.ok) throw new Error('quick view: HTTP ' + response.status);
        return response.text();
      })
      .catch(function (error) {
        /* A failed fetch must not poison the cache — the next click retries. */
        cache.delete(url);
        throw error;
      });
    cache.set(url, promise);
    return promise;
  }

  /* Scripts the fetched section declares, loaded once for the whole page.

     Only `script[src]` is touched. The section also carries an inline
     `<script type="application/json" data-selected-variant>` that product-info
     reads out of the DOM — moving or re-running that would break the variant
     lookup, so it is left exactly where it is. */
  var scriptsLoaded = new Set();

  function ensureScripts(doc) {
    var jobs = [];
    doc.querySelectorAll('script[src]').forEach(function (node) {
      var src = node.src;
      if (!src || scriptsLoaded.has(src)) return;
      scriptsLoaded.add(src);
      if (document.head.querySelector('script[src="' + src + '"]')) return;
      jobs.push(
        new Promise(function (resolve) {
          var el = document.createElement('script');
          el.src = src;
          el.defer = true;
          /* resolve on error too: a missing asset must not leave the shopper
             staring at a spinner. The popup degrades, it does not hang. */
          el.onload = resolve;
          el.onerror = resolve;
          document.head.appendChild(el);
        })
      );
    });
    return Promise.all(jobs);
  }

  /* Repaint the popup's photo when she picks another variant.

     🪤 The listener lives on the DIALOG, not on the input: product-info.js
     REPLACES the markup inside the popup on every variant change, so anything
     bound to the input itself is thrown away after the first pick. Dawn
     dispatches a bubbling `change` on the hidden [name=id] once it has settled
     on a variant, which is the only signal here that is both late enough and
     reliable.

     🪤 `srcset` has to go. Leaving it means the browser keeps serving the OLD
     candidate at the current width and the new `src` is ignored. */
  function bindMedia() {
    var map = {};
    var json = body.querySelector('[data-ltt-qv-media]');
    if (json) {
      try {
        map = JSON.parse(json.textContent || '{}');
      } catch (e) {
        map = {};
      }
    }
    if (!Object.keys(map).length) return;

    function paint() {
      var input = body.querySelector('[name="id"]');
      var img = body.querySelector('.ltt-qv__media img');
      if (!input || !img) return;
      var src = map[input.value];
      if (!src || img.src === src) return;
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = src;
    }

    if (!body.dataset.lttQvMediaBound) {
      body.dataset.lttQvMediaBound = '1';
      body.addEventListener('change', function (event) {
        if (event.target && event.target.name === 'id') paint();
      });
    }
    paint();
  }

  function open(url, trigger) {
    var el = buildDialog();
    lastTrigger = trigger || null;
    body.innerHTML =
      '<div class="ltt-qv-dialog__loading"><span class="ltt-qv-dialog__spinner" aria-hidden="true"></span></div>';
    if (!el.open) el.showModal();
    el.classList.add('is-open');

    fetchMarkup(url)
      .then(function (html) {
        if (!el.open) return;
        /* Parsed out of a document rather than assigned as innerHTML on the
           live node, so the custom elements upgrade once on insertion.

           🔴 But importNode does NOT run <script src> — measured on the live
           homepage 2026-08-19: the two script tags sat in the dialog's DOM and
           `customElements.get('product-info')` was still undefined. That one
           fact caused BOTH reported symptoms, because everything the variant
           picker does lives in product-info.js:
             • the price never changed (moroccan-soap-1 stayed at LE 225 after
               picking "Soap + Lofa", which the server renders at LE 290), and
             • the hidden input[name=id] kept the FIRST variant, so the cheap
               variant was what actually reached the cart.
           product-form.js happens to be on these pages already, which is why
           Add-to-cart still "worked" — it just added the wrong variant.

           So load the section's scripts for real, and only then insert. */
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var content = doc.querySelector('product-info') || doc.body;
        return ensureScripts(doc).then(function () {
          if (!el.open) return;
          body.innerHTML = '';
          body.appendChild(document.importNode(content, true));
          bindMedia();
        });
      })
      .catch(function () {
        /* No silent empty popup: send the shopper to the page that always
           works rather than leaving them looking at a spinner. */
        close();
        window.location.href = url;
      });
  }

  function close() {
    if (dialog && dialog.open) dialog.close();
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-ltt-quickview]');
    if (!trigger) return;
    var url = trigger.getAttribute('data-product-url');
    if (!url) return;
    event.preventDefault();
    open(url, trigger);
  });

  /* Warm the cache before the click. Passive so it never blocks scrolling. */
  ['pointerenter', 'touchstart'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        var trigger = event.target.closest && event.target.closest('[data-ltt-quickview]');
        if (!trigger) return;
        var url = trigger.getAttribute('data-product-url');
        if (url) fetchMarkup(url).catch(function () {});
      },
      { capture: true, passive: true }
    );
  });

  /* See the header comment: this runs before the cart drawer opens. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
      if (data && data.source === 'product-form') close();
    });
  }
})();
